/**
 * Minimal vCard 2.1 / 3.0 / 4.0 parser — enough to extract the fields we need
 * for the CRM contact form (name, last name, email, phone, organization,
 * title/position, city).
 *
 * The full RFC 6350 grammar is complex; this parser deliberately ignores
 * unsupported properties so unknown vendor extensions don't crash sharing.
 */

function unescape(value) {
    return String(value || "")
        .replace(/\\n/gi, "\n")
        .replace(/\\,/g, ",")
        .replace(/\\;/g, ";")
        .replace(/\\\\/g, "\\");
}

function splitLine(line) {
    // Split "PROP;PARAM=VAL:value..." into { prop, params, value }
    const colon = line.indexOf(":");
    if (colon === -1) return null;
    const left = line.slice(0, colon);
    const value = line.slice(colon + 1);
    const parts = left.split(";");
    const prop = parts.shift().toUpperCase();
    const params = {};
    for (const p of parts) {
        const eq = p.indexOf("=");
        if (eq === -1) {
            // bare param (vCard 2.1 style), e.g. "WORK"
            params[p.toUpperCase()] = true;
        } else {
            params[p.slice(0, eq).toUpperCase()] = p.slice(eq + 1);
        }
    }
    return { prop, params, value };
}

function unfold(raw) {
    // Lines beginning with a space or tab are continuations of the previous line.
    const lines = String(raw || "").replace(/\r\n/g, "\n").split("\n");
    const out = [];
    for (const ln of lines) {
        if ((ln.startsWith(" ") || ln.startsWith("\t")) && out.length) {
            out[out.length - 1] += ln.slice(1);
        } else {
            out.push(ln);
        }
    }
    return out;
}

export function parseVCard(text) {
    if (!text || typeof text !== "string") return {};
    const lines = unfold(text);
    const out = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        company_name: "",
        position: "",
        city: "",
    };

    for (const line of lines) {
        if (!line || line.startsWith("BEGIN:") || line.startsWith("END:") || line.startsWith("VERSION:")) {
            continue;
        }
        const parsed = splitLine(line);
        if (!parsed) continue;
        const { prop, value } = parsed;

        switch (prop) {
            case "FN": {
                if (!out.first_name && !out.last_name) {
                    const full = unescape(value).trim();
                    const parts = full.split(/\s+/);
                    out.first_name = parts.shift() || "";
                    out.last_name = parts.join(" ");
                }
                break;
            }
            case "N": {
                // N: Family;Given;Additional;Prefix;Suffix
                const segs = value.split(";").map(unescape);
                const family = (segs[0] || "").trim();
                const given = (segs[1] || "").trim();
                if (given) out.first_name = given;
                if (family) out.last_name = family;
                break;
            }
            case "EMAIL": {
                if (!out.email) out.email = unescape(value).trim();
                break;
            }
            case "TEL": {
                if (!out.phone) out.phone = unescape(value).trim();
                break;
            }
            case "ORG": {
                // ORG: Company;Unit;... — only the company name matters here.
                const segs = value.split(";").map(unescape);
                if (!out.company_name) out.company_name = (segs[0] || "").trim();
                break;
            }
            case "TITLE":
            case "ROLE": {
                if (!out.position) out.position = unescape(value).trim();
                break;
            }
            case "ADR": {
                // ADR: PO;Ext;Street;Locality;Region;PostalCode;Country
                const segs = value.split(";").map(unescape);
                if (!out.city) out.city = (segs[3] || "").trim();
                break;
            }
            default:
                break;
        }
    }
    return out;
}
